import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { spacing } from 'ext-ui/style/spacing'
import React from 'react'
import Dropdown, {
  DropdownInfiniteScrollProps,
  DropdownItemProps,
  DropdownOption,
  DropdownOptionGroup,
  MultiItemProps,
} from 'src/components/Dropdown/Dropdown'
import Trns from 'src/localization/Trns'
import styled, { css, CSSProp } from 'styled-components'

export interface TextOptionDetails {
  text: string
  additionalText?: React.ReactElement
}

export type TextOption = DropdownOption<TextOptionDetails>

export const DropdownOptionDiv = styled.div<{
  nameColor?: string
  nameFont?: string
  isRoot?: boolean
  isUpdated?: boolean
  isSelected?: boolean
  hasAdditionalText?: boolean
}>`
  display: flex;
  gap: ${spacing(1)};
  align-items: center;
  flex-grow: 2;
  overflow: hidden;
  ${({ nameColor, nameFont }): CSSProp =>
    getFontCss(
      14,
      nameColor ?? themeConstants.colors.font_primary,
      nameFont ?? styleConstants.fontFamilies.regular,
    )}

  height: 60px;

  ${({ isRoot }): CSSProp => {
    return isRoot
      ? css`
          height: 100%;
        `
      : css``
  }}

  ${({ isUpdated }): CSSProp => {
    return isUpdated
      ? css`
          ${getFontCss(
            14,
            themeConstants.colors.highlight,
            styleConstants.fontFamilies.regular,
          )};
        `
      : css``
  }}

  ${({ isSelected }): CSSProp => {
    return isSelected
      ? css`
          ${getFontCss(
            14,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
        `
      : css``
  }}

  ${({ hasAdditionalText }): CSSProp => {
    return hasAdditionalText
      ? css`
          height: 30px;
        `
      : css``
  }}
`

const ItemDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 2;
  height: 60px;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TextNoneDropdownItem: React.FC<{ className?: string }> = (
  props,
): React.ReactElement => {
  return (
    <ItemDiv
      className={props.className}
      css={css`
        ${getFontCss(
          14,
          styleConstants.colors.gray_darkest,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      <Trns source={'(none)'} />
    </ItemDiv>
  )
}

const TextDropdownItem: React.FC<DropdownItemProps<TextOptionDetails>> = (
  props,
): React.ReactElement => {
  return (
    <div
      css={css`
        ${props.details?.additionalText !== undefined &&
        !props.isRoot &&
        css`
          margin-top: 10px;
        `}
      `}
    >
      <DropdownOptionDiv
        className={props.className}
        isRoot={props.isRoot}
        isUpdated={!!props.isUpdated}
        isSelected={props.isSelected}
        hasAdditionalText={props.details?.additionalText !== undefined}
      >
        {props.details?.text || props.value}
      </DropdownOptionDiv>
      {props.details?.additionalText !== undefined && (
        <p
          css={css`
            overflow: hidden;
            padding-bottom: ${props.isRoot ? 5 : 15}px;
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};

            ${props.isRoot &&
            css`
              height: 100%;
            `}

            ${!!props.isUpdated &&
            css`
              ${getFontCss(
                12,
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )};
            `}
          `}
        >
          {props.details.additionalText}
        </p>
      )}
    </div>
  )
}

const TextMultiItem: React.FC<MultiItemProps<TextOptionDetails>> = (
  props,
): React.ReactElement => {
  return (
    <DropdownOptionDiv className={props.className} isRoot={true}>
      <span
        css={css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
      >
        {props.options.length === 1 &&
          (props.options[0].details?.text || props.options[0].value)}
        {props.options.length > 1 && (
          <Trns
            source={'${selectedOption}, +${count} more'}
            expressions={{
              selectedOption:
                props.options[0].details?.text || props.options[0].value,
              count: `${props.options.length - 1}`,
            }}
          />
        )}
      </span>
    </DropdownOptionDiv>
  )
}

const textOptionFilter = (option: TextOption, val: string): boolean =>
  option.details !== undefined
    ? option.details.text.toLowerCase().includes(val.toLowerCase())
    : option.value.toLowerCase().includes(val.toLowerCase())

const getTextOptionFilterText = (option: TextOption): string =>
  option.details !== undefined ? option.details.text : option.value

interface TextDropdownProps {
  className?: string
  optionGroups: Array<DropdownOptionGroup<TextOption>>
  chooseOption: (val: string) => void
  placeholder?: string
  fieldLabel?: React.ReactElement | string
  errors?: Array<React.ReactElement | string>
  disabled?: boolean
  isMulti?: boolean
  selectedValues: Set<string>
  baseValues?: Set<string>
  showUpdates?: boolean
  hideWasOnUpdate?: boolean
  isOptional?: boolean
  onResetClick?: () => void
  isCreatable?: boolean
  validateNewOption?: (val: string) => boolean
  newOptionLabel?: React.ReactElement
  id?: string
  'data-testid'?: string
  infiniteScrollProps?: DropdownInfiniteScrollProps
  customEmptyDropdownMessage?: React.ReactElement
  customNoMatchingOptionsDropdownMessage?: React.ReactElement
  deselectAllOptions?: () => void
}

const TextDropdown: React.FC<TextDropdownProps> = (
  props,
): React.ReactElement => {
  return (
    <Dropdown
      id={props.id}
      data-testid={props['data-testid']}
      className={props.className}
      fieldLabel={props.fieldLabel}
      allowNoneProps={
        props.deselectAllOptions !== undefined
          ? {
              NoneDropdownItem: TextNoneDropdownItem,
              deselectAllOptions: props.deselectAllOptions,
            }
          : undefined
      }
      DropdownItem={TextDropdownItem}
      MultiItem={TextMultiItem}
      optionGroups={props.optionGroups}
      isMulti={props.isMulti}
      selectedValues={props.selectedValues}
      chooseOption={props.chooseOption}
      placeholder={props.placeholder}
      errors={props.errors}
      disabled={props.disabled}
      filter={textOptionFilter}
      getFilterText={getTextOptionFilterText}
      baseValues={props.baseValues}
      showUpdates={props.showUpdates}
      hideWasOnUpdate={props.hideWasOnUpdate}
      isOptional={props.isOptional}
      onResetClick={props.onResetClick}
      validateNewOption={
        props.isCreatable ? props.validateNewOption : undefined
      }
      newOptionLabel={props.newOptionLabel}
      infiniteScrollProps={props.infiniteScrollProps}
      customEmptyDropdownMessage={props.customEmptyDropdownMessage}
      customNoMatchingOptionsDropdownMessage={
        props.customNoMatchingOptionsDropdownMessage
      }
    />
  )
}

export default TextDropdown
